import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Dices } from "lucide-react";
import RollPrivacyControl from "./RollPrivacyControl";
import AbilityBreakdownPopover from "./AbilityBreakdownPopover";
import TraitBlurb from "./TraitBlurb";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const RANKS = ["untrained", "trained", "expert", "master", "legendary"];

export default function HomebrewCharacterSheet({ gameId, character, onCharacterUpdate, isGm, members, myMemberId, ruleset }) {
  const [stats, setStats] = useState(null);
  const [rollPrivacy, setRollPrivacy] = useState({ is_private: false, whisper_target_member_id: null });
  const [openAbilityBreakdown, setOpenAbilityBreakdown] = useState(null);
  const [hbAncestries, setHbAncestries] = useState([]);
  const [hbClasses, setHbClasses] = useState([]);
  const modules = ruleset?.rule_modules || {};
  const ancestryObj = hbAncestries.find((a) => a.id === character.homebrew_ancestry_id) || null;
  const classObj = hbClasses.find((c) => c.id === character.homebrew_class_id) || null;

  useEffect(() => {
    api.get(`/games/${gameId}/homebrew/ancestries`).then((r) => setHbAncestries(r.data));
    api.get(`/games/${gameId}/homebrew/classes`).then((r) => setHbClasses(r.data));
  }, [gameId]);

  const loadStats = useCallback(async () => {
    const { data } = await api.get(`/characters/${character.id}/homebrew-stats`);
    setStats(data);
  }, [character.id]);

  useEffect(() => { loadStats(); }, [loadStats, character]);

  const patch = async (updates) => {
    const { data } = await api.patch(`/characters/${character.id}`, updates);
    onCharacterUpdate(data);
  };

  const roll = async (label, modifier) => {
    try {
      await api.post(`/characters/${character.id}/roll`, {
        game_id: gameId, label, modifier,
        is_private: rollPrivacy.is_private, whisper_target_member_id: rollPrivacy.whisper_target_member_id,
      });
      if (rollPrivacy.whisper_target_member_id) {
        const targetName = (members || []).find((m) => m.id === rollPrivacy.whisper_target_member_id)?.display_name || "player";
        toast.success(`Rolled secretly — only you and ${targetName} can see this`);
      } else if (rollPrivacy.is_private) {
        toast.success(isGm ? "Rolled privately — only you can see this" : "Rolled privately — only you and the GM can see this");
      }
    } catch {
      toast.error("Roll failed");
    }
  };

  if (!stats) return <div className="p-6 text-sm text-[#f1e0c5]/40">Loading sheet…</div>;

  const RollButton = ({ label, modifier }) => (
    <button data-testid={`homebrew-roll-${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => roll(label, modifier)} className="w-8 h-8 rounded-full bg-[#D90429]/20 hover:bg-[#D90429] text-[#D90429] hover:text-white flex items-center justify-center shrink-0 transition-colors duration-200">
      <Dices className="w-3.5 h-3.5" />
    </button>
  );

  const renderBlock = (blockId) => {
    if (blockId === "ability_scores" && modules.ability_scores) {
      return (
        <div key={blockId} className="grid grid-cols-3 gap-3">
          {ABILITIES.map((a) => (
            <div key={a} className="relative bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-2 text-center">
              <label className="text-[10px] uppercase text-[#D4AF37]">{a}</label>
              <AbilityBreakdownPopover
                ability={a}
                history={character.boost_history}
                finalValue={character.abilities?.[a] ?? 10}
                open={openAbilityBreakdown === a}
                onToggle={() => setOpenAbilityBreakdown((cur) => (cur === a ? null : a))}
                testId={`homebrew-ability-breakdown-panel-${a}`}
              />
              <Input
                data-testid={`homebrew-ability-${a}`}
                type="number"
                value={character.abilities?.[a] ?? 10}
                onChange={(e) => patch({ abilities: { ...character.abilities, [a]: parseInt(e.target.value, 10) || 10 } })}
                className="bg-black/30 border-[#4a3424]/50 h-8 text-center"
              />
              <div className="flex items-center justify-center gap-1.5 mt-1">
                <span className="text-xs text-[#f1e0c5]/50">{stats.ability_mods?.[a] >= 0 ? "+" : ""}{stats.ability_mods?.[a]}</span>
                <RollButton label={a.toUpperCase()} modifier={stats.ability_mods?.[a] || 0} />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (blockId === "saves" && modules.saves) {
      return (
        <div key={blockId} className="space-y-1.5">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Saving Throws</p>
          {Object.entries(stats.saves || {}).map(([name, s]) => (
            <div key={name} className="flex items-center justify-between gap-2 bg-black/20 border border-[#4a3424]/50 rounded-lg px-3 py-1.5">
              <span className="text-sm capitalize">{name}</span>
              <div className="flex items-center gap-2">
                <Select value={s.rank} onValueChange={(v) => patch({ saves_proficiencies: { ...character.saves_proficiencies, [name]: v } })}>
                  <SelectTrigger data-testid={`homebrew-save-rank-${name}`} className="bg-black/30 border-[#4a3424]/50 h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-xs w-8 text-right">{s.modifier >= 0 ? "+" : ""}{s.modifier}</span>
                <RollButton label={`${name} Save`} modifier={s.modifier} />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (blockId === "ac_hp" && (modules.ac || modules.hp)) {
      return (
        <div key={blockId} className="flex gap-3">
          {modules.ac && (
            <div className="flex-1 bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-3 text-center">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40">AC</p>
              <p className="font-display text-3xl text-[#D4AF37]">{stats.ac}</p>
            </div>
          )}
          {modules.hp && (
            <div className="flex-[2] bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-3">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Hit Points</p>
              <div className="flex items-center gap-2">
                <Input data-testid="homebrew-hp-current" type="number" value={character.hp_current ?? ""} placeholder="current" onChange={(e) => patch({ hp_current: parseInt(e.target.value, 10) || 0 })} className="bg-black/30 border-[#4a3424]/50 h-8" />
                <span className="text-[#f1e0c5]/40">/</span>
                <Input data-testid="homebrew-hp-max" type="number" value={character.hp_max ?? ""} placeholder="max" onChange={(e) => patch({ hp_max: parseInt(e.target.value, 10) || 0 })} className="bg-black/30 border-[#4a3424]/50 h-8" />
              </div>
            </div>
          )}
        </div>
      );
    }
    if (blockId === "skills" && modules.skills) {
      return (
        <div key={blockId} className="space-y-1.5">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Skills</p>
          <div className="space-y-1 max-h-72 overflow-y-auto scrollbar-thin">
            {Object.entries(stats.skills || {}).map(([name, s]) => (
              <div key={name} className="flex items-center justify-between gap-2 bg-black/20 border border-[#4a3424]/50 rounded-lg px-3 py-1">
                <span className="text-sm capitalize">{name} <span className="text-[10px] text-[#f1e0c5]/30 uppercase">{s.ability}</span></span>
                <div className="flex items-center gap-2">
                  <Select value={s.rank} onValueChange={(v) => patch({ skill_proficiencies: { ...character.skill_proficiencies, [name]: v } })}>
                    <SelectTrigger data-testid={`homebrew-skill-rank-${name}`} className="bg-black/30 border-[#4a3424]/50 h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-xs w-8 text-right">{s.modifier >= 0 ? "+" : ""}{s.modifier}</span>
                  <RollButton label={name} modifier={s.modifier} />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (blockId === "spellcasting" && modules.spellcasting) {
      const sc = stats.spellcasting || {};
      return (
        <div key={blockId} className="bg-gradient-to-b from-[#2a1c12] to-[#1f150c] border-2 border-[#4a3424] border-b-[#1a1008] border-r-[#1a1008] rounded-xl shadow-[inset_0_2px_8px_rgba(0,0,0,0.4)] p-3 space-y-2">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Spellcasting</p>
          <div className="flex items-center gap-2">
            <Select value={character.spell_ability || "int"} onValueChange={(v) => patch({ spell_ability: v })}>
              <SelectTrigger data-testid="homebrew-spell-ability" className="bg-black/30 border-[#4a3424]/50 h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{ABILITIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={character.spell_proficiency || "untrained"} onValueChange={(v) => patch({ spell_proficiency: v })}>
              <SelectTrigger data-testid="homebrew-spell-rank" className="bg-black/30 border-[#4a3424]/50 h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{RANKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span>DC {sc.dc}</span>
            <span className="flex items-center gap-1.5">Attack {sc.attack >= 0 ? "+" : ""}{sc.attack} <RollButton label="Spell Attack" modifier={sc.attack || 0} /></span>
          </div>
        </div>
      );
    }
    if (blockId === "custom_fields" && (ruleset?.custom_fields || []).length > 0) {
      return (
        <div key={blockId} className="space-y-2">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Custom Fields</p>
          {ruleset.custom_fields.map((f) => {
            const value = character.homebrew_values?.[f.id];
            const setValue = (v) => patch({ homebrew_values: { ...character.homebrew_values, [f.id]: v } });
            return (
              <div key={f.id} className="bg-black/20 border border-[#4a3424]/50 rounded-lg p-2">
                <label className="text-[10px] uppercase text-[#f1e0c5]/40 block mb-1">{f.label}</label>
                {f.type === "textarea" && <Textarea data-testid={`homebrew-value-${f.id}`} value={value || ""} onChange={(e) => setValue(e.target.value)} className="bg-black/30 border-[#4a3424]/50 text-sm" />}
                {f.type === "text" && <Input data-testid={`homebrew-value-${f.id}`} value={value || ""} onChange={(e) => setValue(e.target.value)} className="bg-black/30 border-[#4a3424]/50 h-8 text-sm" />}
                {f.type === "checkbox" && (
                  <Checkbox data-testid={`homebrew-value-${f.id}`} checked={!!value} onCheckedChange={setValue} />
                )}
                {f.type === "dropdown" && (
                  <Select value={value || ""} onValueChange={setValue}>
                    <SelectTrigger data-testid={`homebrew-value-${f.id}`} className="bg-black/30 border-[#4a3424]/50 h-8 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{(f.options || "").split(",").map((o) => o.trim()).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {f.type === "number" && (
                  <div className="flex items-center gap-2">
                    <Input data-testid={`homebrew-value-${f.id}`} type="number" value={value ?? ""} onChange={(e) => setValue(parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-[#4a3424]/50 h-8 text-sm" />
                    {f.rollable && <RollButton label={f.label} modifier={parseInt(value, 10) || 0} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-4 h-full overflow-y-auto scrollbar-thin space-y-4" data-testid="homebrew-character-sheet">
      <div className="flex items-center justify-between gap-2">
        <Input data-testid="homebrew-character-name" value={character.name} onChange={(e) => patch({ name: e.target.value })} className="bg-black/30 border-[#4a3424]/50 font-display text-lg flex-1" />
        <Input data-testid="homebrew-character-level" type="number" min={1} value={character.level || 1} onChange={(e) => patch({ level: parseInt(e.target.value, 10) || 1 })} className="bg-black/30 border-[#4a3424]/50 h-9 w-16 text-center" title="Level" />
      </div>
      <RollPrivacyControl isGm={isGm} members={members} myMemberId={myMemberId} value={rollPrivacy} onChange={setRollPrivacy} />
      {(ancestryObj || classObj) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {ancestryObj && (
            <div>
              <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Ancestry: {ancestryObj.name}</p>
              <TraitBlurb
                testId="homebrew-ancestry-trait-blurb"
                description={ancestryObj.description}
                facts={[`Speed ${ancestryObj.speed}ft`, `HP +${ancestryObj.hp}`, ...(ancestryObj.senses || [])]}
              />
            </div>
          )}
          {classObj && (
            <div>
              <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1">Class: {classObj.name}</p>
              <TraitBlurb
                testId="homebrew-class-trait-blurb"
                description={classObj.description}
                facts={[`Key Ability: ${(classObj.key_ability || []).join("/") || "any"}`, `${classObj.hp_per_level} HP/Level`]}
              />
            </div>
          )}
        </div>
      )}
      {(ruleset?.sheet_layout || []).map(renderBlock)}
    </div>
  );
}
